import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getSessionId,
  getSessionIdFromUrl,
  initializeSession,
  pathWithSessionId,
} from '../utils/sessionManager';
import { getApiBaseUrl } from '../utils/apiConfig';
import { compressDataUrl } from '../utils/imageCompress';
import { uploadSkinMacroCapture } from '../utils/skinAnalysisApi';
import {
  isNyafazNativeMicroscopeAvailable,
  captureNativeMicroscopeInline,
  startNativeScalpInlinePreview,
  updateNativeScalpInlineRect,
  stopNativeScalpInlinePreview,
  measurePreviewRect,
  supportsNativeInlineScalpPreview,
  nativeTargetDisplayName,
  requestNativeCameraPermission,
  requestNativeUsbPermission,
  listNativeUsbCameras,
  pickNativeUsbMicroscopeId,
  waitForNyafazNativeBridge,
} from '../../shared/nyafazNativeBridge';
import { isPresentationMode } from '../../presentation/presentationSession';
import NaturalsCameraSelect from '../../shared/EnterpriseCameraSelect';
import {
  parseCameraSelectValue,
  resolveMacroCameraSelectValue,
} from '../../shared/cameraSelectValues';
import {
  hasCameraPermissionGranted,
  isLegacyCustomAndroidWebView,
  isSalonApkMacroKiosk,
  listMicroscopeVideoInputs,
  openBuiltinFaceCameraStream,
  openMicroscopeVideoStream,
  readStoredMacroUsbDeviceId,
  readStoredMacroNativeUsbId,
  requestCameraAccessWithPrompt,
  storeMacroUsbDeviceId,
  storeMacroNativeUsbId,
  waitForPreviewLayout,
} from '../../shared/usbMicroscopeCamera';
import SalonHeaderActions from '../components/SalonHeaderActions';
import FaceIdentityBrandLogo from '../../face-identity/components/FaceIdentityBrandLogo';
import { useFaceIdentityOrgLogo } from '../../face-identity/hooks/useFaceIdentityOrgLogo';
import { useSkinSession } from '../context/SkinSessionContext';
import { goSalonHubForNewUser } from '../utils/salonHomeNav';
import './SkinMacroCapturePage.css';
import '../styles/PageBackground.css';
import '../styles/Typography.css';
import '../styles/ActionButtons.css';

const SKIN_REF_PUBLIC = `${process.env.PUBLIC_URL || ''}/skin-reference`;

const MACRO_SEQUENCE = ['forehead', 'cheek'];

const MACRO_GUIDE = {
  forehead: {
    refImg: `${SKIN_REF_PUBLIC}/forehead.png`,
    lede: 'Place the USB microscope on the center of the forehead — show pores and texture clearly.',
    tip: 'Keep the lens gently on the forehead skin.',
    refCaption: 'Forehead reference',
  },
  cheek: {
    refImg: `${SKIN_REF_PUBLIC}/cheek.png`,
    lede: 'Place the USB microscope on the cheek (mid-face) — show skin texture and tone clearly.',
    tip: 'Rest the lens lightly on the cheek, avoiding the eye area.',
    refCaption: 'Cheek reference',
  },
};

function isNyufazMacroNativeHost() {
  if (typeof window === 'undefined') return false;
  return (
    isSalonApkMacroKiosk() ||
    isPresentationMode() ||
    Boolean(window.__nyafazBridgeReady)
  );
}

function resolveMacroNativeUsbDeviceId(cameras, storedId) {
  const list = Array.isArray(cameras) ? cameras : [];
  const idOf = (d) => String(d?.nativeId ?? d?.deviceId ?? d?.id ?? '').trim();
  const stored = String(storedId || readStoredMacroNativeUsbId() || '').trim();
  if (stored && list.some((d) => idOf(d) === stored)) return stored;
  const pick = pickNativeUsbMicroscopeId(list);
  if (pick) return String(pick);
  return list.length ? idOf(list[0]) : '';
}

const MACRO_TIPS = [
  { id: 'focus', label: 'Center the target area in the frame.' },
  { id: 'steady', label: 'Hold the microscope steady.' },
  { id: 'light', label: 'Use bright, even lighting.' },
  { id: 'contact', label: 'Gently rest the lens on the skin.' },
];

function IconBack() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function fileToJpegDataUrl(file, { maxSide = 1600, quality = 0.9 } = {}) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = async () => {
      try {
        const raw = String(reader.result || '');
        const compressed = await compressDataUrl(raw, { maxSide, quality, forAnalysis: true });
        resolve(compressed);
      } catch (e) {
        reject(e);
      }
    };
    reader.readAsDataURL(file);
  });
}

export default function SkinMacroCapturePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useSkinSession();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const previewFrameRef = useRef(null);
  /** Once user/native explicitly falls back, never re-enable via bridge polling. */
  const nativeDisabledRef = useRef(false);

  const [sessionId, setSessionId] = useState(
    () =>
      location.state?.sessionId ||
      getSessionIdFromUrl(location) ||
      getSessionId(),
  );

  const capturedImage = location.state?.capturedImage ?? null;
  const sideCaptures = location.state?.sideCaptures ?? null;
  const userData = location.state?.userData ?? null;
  const aqiData = location.state?.aqiData ?? null;

  const initialStep = Math.max(
    0,
    MACRO_SEQUENCE.indexOf(location.state?.macroStep || 'forehead'),
  );

  const [stepIndex, setStepIndex] = useState(initialStep);
  const [captures, setCaptures] = useState(() => ({
    forehead: location.state?.macroCaptures?.forehead || null,
    cheek: location.state?.macroCaptures?.cheek || null,
  }));
  const [preview, setPreview] = useState(null);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState('');
  const [webPreviewReady, setWebPreviewReady] = useState(false);
  /** null=checking, false=need Allow tap, true=permission ready */
  const [cameraAllowed, setCameraAllowed] = useState(null);
  const [allowBusy, setAllowBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [micUnavailable, setMicUnavailable] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [brightness, setBrightness] = useState(100);
  const [frozen, setFrozen] = useState(false);
  /** false = tablet/device cameras (front/rear/USB in browser list); true = Salon APK UVC bridge */
  const [useNativeUsb, setUseNativeUsb] = useState(() => isSalonApkMacroKiosk());
  const [nativeOpening, setNativeOpening] = useState(false);
  const [nativeCooldown, setNativeCooldown] = useState(false);
  const [nativeUsbDevices, setNativeUsbDevices] = useState([]);
  const [selectedNativeUsbId, setSelectedNativeUsbId] = useState(() => readStoredMacroNativeUsbId());
  const [webFacing, setWebFacing] = useState('front');

  const target = MACRO_SEQUENCE[Math.min(stepIndex, MACRO_SEQUENCE.length - 1)];
  const regionName = nativeTargetDisplayName(target);
  const guide = MACRO_GUIDE[target] || MACRO_GUIDE.forehead;
  const totalSteps = MACRO_SEQUENCE.length;
  const step = stepIndex + 1;
  const isLast = stepIndex >= MACRO_SEQUENCE.length - 1;

  const goToAnalysis = useCallback(
    (macroCaptures, { skipMacro = false } = {}) => {
      const hasCaptures =
        !skipMacro
        && macroCaptures
        && typeof macroCaptures === 'object'
        && (macroCaptures.forehead || macroCaptures.cheek);
      navigate(pathWithSessionId('/skin-analysis', sessionId), {
        state: {
          capturedImage,
          sideCaptures: sideCaptures || undefined,
          userData,
          sessionId,
          aqiData,
          macroCaptures: hasCaptures ? macroCaptures : undefined,
          macroSkipped: skipMacro || undefined,
          ...(token && { token }),
        },
        replace: true,
      });
    },
    [navigate, sessionId, capturedImage, sideCaptures, userData, aqiData, token],
  );

  const handleSkipMacro = useCallback(() => {
    goToAnalysis(null, { skipMacro: true });
  }, [goToAnalysis]);

  useEffect(() => {
    if (!sessionId || !capturedImage) {
      navigate(pathWithSessionId('/capture', sessionId), { replace: true });
    }
  }, [sessionId, capturedImage, navigate]);

  useEffect(() => {
    const api = getApiBaseUrl();
    if (!api) return;
    const initial =
      location.state?.sessionId ||
      getSessionIdFromUrl(location) ||
      getSessionId();
    if (!initial) return;
    initializeSession(api, token ? { app_session_token: token } : {})
      .then((sid) => {
        if (sid) setSessionId(sid);
      })
      .catch(() => {
        setError('Could not verify salon session. Go back and try again.');
      });
  }, [location, token]);

  const activateNativeUsbMacro = useCallback((nativeDevices) => {
    const list = Array.isArray(nativeDevices) ? nativeDevices : [];
    if (!list.length || nativeDisabledRef.current) return false;
    if (!isNyafazNativeMicroscopeAvailable()) return false;
    const nativeId = resolveMacroNativeUsbDeviceId(list, selectedNativeUsbId);
    if (!nativeId) return false;
    setNativeUsbDevices(list);
    setUseNativeUsb(true);
    setSelectedNativeUsbId(nativeId);
    storeMacroNativeUsbId(nativeId);
    setMicUnavailable(false);
    stopStream();
    return true;
  }, [selectedNativeUsbId]);

  const bootNativeMacro = useCallback(async () => {
    await waitForNyafazNativeBridge(15000);
    if (nativeDisabledRef.current || !isNyafazNativeMicroscopeAvailable()) return false;
    await requestNativeCameraPermission();
    await requestNativeUsbPermission();
    const cams = await listNativeUsbCameras(8000);
    setNativeUsbDevices(cams);
    if (cams.length && activateNativeUsbMacro(cams)) {
      setCameraAllowed(true);
      setError(null);
      return true;
    }
    setUseNativeUsb(false);
    const granted = await hasCameraPermissionGranted();
    setCameraAllowed(granted);
    setMicUnavailable(false);
    return false;
  }, [activateNativeUsbMacro]);

  // Custom Android WebViews often skip the "Allow camera" sheet unless getUserMedia
  // runs from a user tap. Branded devices usually prompt (or already granted).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isNyufazMacroNativeHost()) {
        const ok = await bootNativeMacro();
        if (cancelled) return;
        if (ok) return;
      }
      const granted = await hasCameraPermissionGranted();
      if (cancelled) return;
      if (isLegacyCustomAndroidWebView() || !granted) {
        setCameraAllowed(false);
      } else {
        setCameraAllowed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bootNativeMacro]);

  const onAllowCamera = useCallback(async () => {
    setAllowBusy(true);
    setError(null);
    try {
      await requestNativeCameraPermission();
      // GM76S/Rockchip: getUserMedia binds ExtCam and steals the USB MIC from UVC.
      // When the Salon APK bridge is present, skip web getUserMedia and use native UVC.
      if (isNyafazNativeMicroscopeAvailable() && !nativeDisabledRef.current) {
        await requestNativeUsbPermission();
        const cams = await listNativeUsbCameras(8000);
        setNativeUsbDevices(cams);
        if (cams.length) {
          activateNativeUsbMacro(cams);
          setCameraAllowed(true);
          setMicUnavailable(false);
          return;
        }
        setUseNativeUsb(false);
        const okUsbFallback = await requestCameraAccessWithPrompt({ timeoutMs: 25000 });
        if (!okUsbFallback) {
          setError('Camera permission was not granted. Tap Allow when the system asks, then try again.');
          setCameraAllowed(false);
          return;
        }
        setCameraAllowed(true);
        setMicUnavailable(false);
        setWebFacing('front');
        return;
      }
      if (isNyufazMacroNativeHost()) {
        const ok = await bootNativeMacro();
        if (ok) return;
      }
      const ok = await requestCameraAccessWithPrompt({ timeoutMs: 25000 });
      if (!ok) {
        setError('Camera permission was not granted. Tap Allow when the system asks, then try again.');
        setCameraAllowed(false);
        return;
      }
      setCameraAllowed(true);
      setMicUnavailable(false);
    } catch (e) {
      setError(e?.message || 'Could not request camera permission.');
      setCameraAllowed(false);
    } finally {
      setAllowBusy(false);
    }
  }, [activateNativeUsbMacro, bootNativeMacro]);

  const forceWebCameraFallback = useCallback((message) => {
    nativeDisabledRef.current = true;
    try {
      stopNativeScalpInlinePreview();
    } catch {
      /* ignore */
    }
    setUseNativeUsb(false);
    setNativeOpening(false);
    if (message) setError(message);
  }, []);

  // Stop native overlay only when leaving native mode or showing a still preview.
  useEffect(() => {
    if (preview || !useNativeUsb) {
      try {
        stopNativeScalpInlinePreview();
      } catch {
        /* ignore */
      }
    }
  }, [preview, useNativeUsb]);

  useEffect(() => {
    if (cameraAllowed !== true || preview) return undefined;
    if (!isNyafazNativeMicroscopeAvailable() || nativeDisabledRef.current) return undefined;
    let cancelled = false;
    (async () => {
      const cams = await listNativeUsbCameras(4500);
      if (cancelled) return;
      setNativeUsbDevices(cams);
    })();
    return () => {
      cancelled = true;
    };
  }, [cameraAllowed, preview]);

  useEffect(() => {
    if (cameraAllowed !== true || !useNativeUsb || preview) return undefined;
    let cancelled = false;

    const pushPreview = async () => {
      const rect = await waitForPreviewLayout(() => previewFrameRef.current, { timeoutMs: 4000 });
      if (cancelled) return;
      try {
        let list = nativeUsbDevices;
        if (!list.length) {
          list = await listNativeUsbCameras(5000);
          if (cancelled) return;
          if (list.length) setNativeUsbDevices(list);
        }
        const nativeId = resolveMacroNativeUsbDeviceId(list, selectedNativeUsbId);
        if (!list.length && !nativeId) {
          setError('USB microscope not listed yet. Unplug/replug OTG, then tap Allow again.');
          setMicUnavailable(true);
          return;
        }
        setMicUnavailable(false);
        setError(null);
        if (supportsNativeInlineScalpPreview()) {
          startNativeScalpInlinePreview({
            target,
            label: nativeTargetDisplayName(target),
            rect,
            ...(nativeId ? { nativeId } : {}),
            preferExternal: true,
          });
        }
      } catch {
        /* bridge not ready */
      }
    };

    const syncRect = () => {
      const rect = measurePreviewRect(previewFrameRef.current);
      if (rect) updateNativeScalpInlineRect(rect);
    };

    pushPreview();
    const t1 = window.setTimeout(pushPreview, 900);
    window.addEventListener('resize', syncRect);
    window.addEventListener('orientationchange', syncRect);
    const ro =
      typeof ResizeObserver !== 'undefined' && previewFrameRef.current
        ? new ResizeObserver(syncRect)
        : null;
    if (previewFrameRef.current && ro) ro.observe(previewFrameRef.current);

    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.removeEventListener('resize', syncRect);
      window.removeEventListener('orientationchange', syncRect);
      ro?.disconnect();
      // Do not stopNative here on region change — teardown is handled separately.
    };
  }, [cameraAllowed, useNativeUsb, target, preview, selectedNativeUsbId, nativeUsbDevices]);

  useEffect(() => () => {
    try {
      stopNativeScalpInlinePreview();
    } catch {
      /* ignore */
    }
  }, []);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const startCamera = useCallback(async (id, facingMode) => {
    stopStream();
    setError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setMicUnavailable(true);
        setError('Microscope unavailable. You can continue with face analysis.');
        return;
      }
      const wantId = typeof id === 'string' ? id.trim() : '';
      let stream;
      if (wantId) {
        stream = await openMicroscopeVideoStream({
          deviceId: wantId,
          timeoutMs: 12000,
        });
        storeMacroUsbDeviceId(wantId);
      } else if (facingMode === 'environment' || facingMode === 'user') {
        stream = await openBuiltinFaceCameraStream({
          facing: facingMode,
          timeoutMs: 12000,
        });
        storeMacroUsbDeviceId('');
      } else {
        setError('Choose a camera from the list to start preview.');
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setMicUnavailable(false);
      setWebPreviewReady(true);
    } catch {
      setMicUnavailable(true);
      setError('Microscope unavailable. Plug in the USB microscope via OTG, then tap Try Again.');
    }
  }, []);

  // Web: list cameras only — preview starts when staff picks a camera from the dropdown.
  useEffect(() => {
    if (cameraAllowed !== true || useNativeUsb) return undefined;
    let cancelled = false;

    const bootWebCams = async () => {
      try {
        const cams = await listMicroscopeVideoInputs();
        if (cancelled) return;
        setDevices(cams);

        const usable = cams.filter((d) => d?.deviceId);
        if (!usable.length) {
          setMicUnavailable(true);
          setWebPreviewReady(false);
          setError(
            isNyafazNativeMicroscopeAvailable()
              ? 'No device camera listed. Switch to “USB microscope (Salon app)” or connect a camera.'
              : 'No camera found. Connect the USB microscope via OTG, then refresh the list.',
          );
          return;
        }

        setMicUnavailable(false);
        const stored = readStoredMacroUsbDeviceId();
        const storedOk = stored && usable.some((d) => d.deviceId === stored);
        if (storedOk) {
          setDeviceId(stored);
          if (!cancelled) {
            await startCamera(stored);
          }
        } else {
          setDeviceId('');
          setWebFacing('front');
          if (!cancelled) {
            await startCamera('', 'user');
          }
        }
        setError(null);
      } catch {
        if (!cancelled) {
          setMicUnavailable(true);
          setWebPreviewReady(false);
          setError('Could not list cameras. Tap Try Again or switch to USB microscope mode.');
        }
      }
    };

    void bootWebCams();

    const onDeviceChange = () => {
      if (cancelled) return;
      void bootWebCams();
    };
    try {
      navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceChange);
    } catch {
      /* ignore */
    }

    return () => {
      cancelled = true;
      stopStream();
      setWebPreviewReady(false);
      try {
        navigator.mediaDevices?.removeEventListener?.('devicechange', onDeviceChange);
      } catch {
        /* ignore */
      }
    };
  }, [cameraAllowed, useNativeUsb, startCamera]);

  const handleWebCameraPick = useCallback(
    (id) => {
      const next = typeof id === 'string' ? id.trim() : '';
      setDeviceId(next);
      setError(null);
      if (!next) {
        stopStream();
        setWebPreviewReady(false);
        return;
      }
      storeMacroUsbDeviceId(next);
      void startCamera(next);
    },
    [startCamera],
  );

  const macroCameraSelectValue = resolveMacroCameraSelectValue({
    useNativeUsb,
    nativeId: selectedNativeUsbId,
    deviceId,
    webFacing,
  });

  const handleMacroCameraSelectChange = useCallback(
    async (rawValue) => {
      const parsed = parseCameraSelectValue(rawValue);
      if (parsed.kind === 'none') return;
      setError(null);
      stopStream();
      setWebPreviewReady(false);

      if (parsed.kind === 'native') {
        if (!isNyafazNativeMicroscopeAvailable() || nativeDisabledRef.current) {
          setError('USB microscope (Salon app) is not available on this device.');
          return;
        }
        try {
          stopNativeScalpInlinePreview();
        } catch {
          /* ignore */
        }
        setUseNativeUsb(true);
        setDeviceId('');
        setSelectedNativeUsbId(parsed.nativeId);
        storeMacroNativeUsbId(parsed.nativeId);
        return;
      }

      try {
        stopNativeScalpInlinePreview();
      } catch {
        /* ignore */
      }
      setUseNativeUsb(false);
      setSelectedNativeUsbId('');

      if (parsed.kind === 'device') {
        await handleWebCameraPick(parsed.deviceId);
        return;
      }

      const nextFacing = parsed.facing === 'rear' ? 'rear' : 'front';
      setWebFacing(nextFacing);
      setDeviceId('');
      storeMacroUsbDeviceId('');
      const mode = nextFacing === 'rear' ? 'environment' : 'user';
      await startCamera('', mode);
    },
    [handleWebCameraPick, startCamera],
  );

  const refreshMacroCameras = useCallback(async () => {
    setError(null);
    try {
      if (isNyafazNativeMicroscopeAvailable() && !nativeDisabledRef.current) {
        const nativeCams = await listNativeUsbCameras(4500);
        setNativeUsbDevices(nativeCams);
      }
    } catch {
      /* ignore */
    }
    try {
      const cams = await listMicroscopeVideoInputs();
      setDevices(cams);
      const usable = cams.filter((d) => d?.deviceId);
      if (!usable.length && !isNyafazNativeMicroscopeAvailable()) {
        setMicUnavailable(true);
        setError('No camera found. Connect the USB microscope via OTG, then tap Refresh cameras.');
      } else {
        setMicUnavailable(false);
      }
    } catch {
      setError('Could not refresh cameras. Check OTG connection and try again.');
    }
  }, []);

  const snap = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.filter = `brightness(${brightness}%)`;
    ctx.drawImage(video, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.9);
  };

  const advanceAfterSave = useCallback(
    (nextCaptures) => {
      if (isLast) {
        goToAnalysis(nextCaptures);
        return;
      }
      setStepIndex((i) => i + 1);
      setPreview(null);
      setError(null);
      setNativeCooldown(true);
      window.setTimeout(() => setNativeCooldown(false), 900);
    },
    [isLast, goToAnalysis],
  );

  const stageCapturePreview = useCallback(async (dataUrl) => {
    setBusy(true);
    setError(null);
    try {
      const compressed = await compressDataUrl(dataUrl, { maxSide: 1600, quality: 0.9, forAnalysis: true });
      setPreview(compressed);
    } catch (e) {
      setError(e?.message || 'Could not prepare image');
    } finally {
      setBusy(false);
      setNativeOpening(false);
    }
  }, []);

  const confirmPendingCapture = useCallback(async () => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      await uploadSkinMacroCapture({
        sessionId,
        region: target,
        image: preview,
        appSessionToken: token || null,
      });
      const nextCaptures = { ...captures, [target]: preview };
      setCaptures(nextCaptures);
      advanceAfterSave(nextCaptures);
    } catch (e) {
      const apiMsg = e?.apiResponse?.error;
      setError(apiMsg || e.message || 'Could not save capture');
    } finally {
      setBusy(false);
    }
  }, [sessionId, target, token, captures, preview, advanceAfterSave]);

  const captureUsbInline = useCallback(async () => {
    if (!isNyafazNativeMicroscopeAvailable() || nativeDisabledRef.current) {
      if (isNyufazMacroNativeHost()) {
        await waitForNyafazNativeBridge(8000);
      }
      if (!isNyafazNativeMicroscopeAvailable() || nativeDisabledRef.current) {
        forceWebCameraFallback('USB microscope bridge not available — trying device camera…');
        return;
      }
    }
    setNativeOpening(true);
    setError(null);
    setMicUnavailable(false);
    try {
      const cams = nativeUsbDevices.length ? nativeUsbDevices : await listNativeUsbCameras(4000);
      const nativeId = resolveMacroNativeUsbDeviceId(cams, selectedNativeUsbId);
      if (!cams.length) {
        throw new Error('USB microscope not detected. Check OTG cable, then retry.');
      }
      const rect = await waitForPreviewLayout(() => previewFrameRef.current, { timeoutMs: 2500 });
      if (supportsNativeInlineScalpPreview()) {
        startNativeScalpInlinePreview({
          target,
          label: nativeTargetDisplayName(target),
          rect,
          ...(nativeId ? { nativeId } : {}),
          preferExternal: true,
        });
      }
      const dataUrl = await captureNativeMicroscopeInline({
        target,
        label: nativeTargetDisplayName(target),
        preferInline: true,
        // Short timeout: custom Android UVC stacks often never callback.
        timeoutMs: 12000,
      });
      await stageCapturePreview(dataUrl);
    } catch (e) {
      setNativeOpening(false);
      const msg = e?.message || '';
      // On Rockchip, web fallback often cannot see USB after UVC — retry native first.
      setError(
        msg.includes('timed out') || msg.includes('cancel')
          ? 'USB capture timed out. Keep the microscope connected and tap Capture again.'
          : msg || 'USB microscope capture failed. Tap Capture again.',
      );
      setMicUnavailable(true);
    }
  }, [stageCapturePreview, target, forceWebCameraFallback, nativeUsbDevices, selectedNativeUsbId]);

  const onCapture = async () => {
    if (useNativeUsb) {
      await captureUsbInline();
      return;
    }
    const dataUrl = snap();
    if (!dataUrl) {
      setError('Microscope unavailable. You can continue with face analysis.');
      return;
    }
    await stageCapturePreview(dataUrl);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const dataUrl = await fileToJpegDataUrl(file);
      await stageCapturePreview(dataUrl);
    } catch (err) {
      setError(err.message || 'Upload failed');
    }
  };

  const captureBusy = busy || nativeOpening || nativeCooldown;

  const handleBack = () => {
    navigate(pathWithSessionId('/capture', sessionId), {
      state: {
        capturedImage,
        userData,
        sessionId,
        aqiData,
        ...(token && { token }),
      },
    });
  };

  const handleHome = () => {
    goSalonHubForNewUser(navigate, { sessionId, token: token || null });
  };

  const targetReason = `We're capturing the ${regionName.toLowerCase()} area. Place the USB microscope on the ${regionName.toLowerCase()}. ${guide.tip}`;

  return (
    <div className="skin-macro-page page-bg skin-macro-capture">
      <header className="skin-macro-header" role="banner">
        <button type="button" className="skin-macro-back" aria-label="Back" onClick={handleBack}>
          <IconBack />
        </button>
        <div className="skin-macro-progress" aria-label="Progress">
          <span className="is-done">1. Face Scan</span>
          <span className="is-on">2. Skin Detail</span>
          <span>3. Results</span>
        </div>
        <div className="skin-macro-header-right">
          <FaceIdentityBrandLogo
            showLogo={showLogo}
            orgLogoUrl={orgLogoUrl}
            onLogoError={onLogoError}
            className="skin-macro-brand-logo"
            imgClassName="skin-macro-brand-logo-img"
            fallbackClassName="skin-macro-brand-fallback"
          />
          <SalonHeaderActions homeButtonClassName="skin-macro-home" onHome={handleHome} />
        </div>
      </header>

      <div className="skin-macro-chips" role="list" aria-label="Skin capture progress">
        {MACRO_SEQUENCE.map((id, i) => (
          <span
            key={id}
            className={`skin-macro-chip ${target === id ? 'is-active' : ''} ${i < stepIndex ? 'is-done' : ''}`}
          >
            {i + 1}. {nativeTargetDisplayName(id)}
          </span>
        ))}
      </div>

      <div className="skin-macro-layout">
        <section className="skin-macro-preview-card">
          <h2 className="skin-macro-preview-title">Live skin preview</h2>

          {cameraAllowed === true && (
            <div className="skin-macro-camera-dock">
              <NaturalsCameraSelect
                label="Camera"
                value={macroCameraSelectValue}
                devices={devices}
                nativeDevices={
                  isNyafazNativeMicroscopeAvailable() && !nativeDisabledRef.current
                    ? nativeUsbDevices
                    : []
                }
                onChange={handleMacroCameraSelectChange}
                onRefresh={refreshMacroCameras}
              />
            </div>
          )}

          <div ref={previewFrameRef} className="skin-macro-frame">
            {cameraAllowed !== true && !preview ? (
              <div className="skin-macro-allow-panel">
                <p className="skin-macro-allow-title">Allow camera for USB microscope</p>
                <p className="skin-macro-allow-copy">
                  Tap Allow so this device can use the USB microscope. On some tablets the system
                  permission only appears after you tap.
                </p>
                <button
                  type="button"
                  className="skin-macro-btn primary"
                  disabled={allowBusy || cameraAllowed === null}
                  onClick={onAllowCamera}
                >
                  {allowBusy ? 'Waiting for permission…' : cameraAllowed === null ? 'Checking…' : 'Allow camera access'}
                </button>
              </div>
            ) : preview ? (
              <img src={preview} alt={`${regionName} capture`} className="skin-macro-media" />
            ) : useNativeUsb ? (
              <div className="skin-macro-native-panel">
                <p className="skin-macro-native-title">
                  {selectedNativeUsbId ? 'USB live preview' : 'Select a USB camera'}
                </p>
                <p className="skin-macro-native-copy">
                  {selectedNativeUsbId
                    ? supportsNativeInlineScalpPreview()
                      ? `Place the microscope on the ${regionName.toLowerCase()} — live view appears here. Tap Capture when ready.`
                      : 'Connect the USB microscope via OTG, then tap Capture.'
                    : 'Pick the USB microscope from the list above to start live preview.'}
                </p>
              </div>
            ) : !webPreviewReady ? (
              <div className="skin-macro-native-panel">
                <p className="skin-macro-native-title">Select a camera</p>
                <p className="skin-macro-native-copy">
                  Pick front, rear, a connected camera, or USB microscope from the list above.
                </p>
              </div>
            ) : (
              <video
                ref={videoRef}
                className="skin-macro-media"
                playsInline
                muted
                autoPlay
                style={{ filter: `brightness(${brightness}%)` }}
              />
            )}
            <div className="skin-macro-corners" aria-hidden>
              <i />
              <i />
              <i />
              <i />
            </div>
            <span className="skin-macro-region-label">{regionName}</span>
          </div>
          <div className="skin-macro-callout" role="status">
            <span className="skin-macro-callout-ico" aria-hidden />
            <p>
              Skin detail {Math.min(step, totalSteps)}/{totalSteps} · {regionName}. {targetReason}
            </p>
          </div>
        </section>

        <aside className="skin-macro-guide-card">
          <h2 className="skin-macro-guide-title">How to capture</h2>
          <p className="skin-macro-guide-sub">Follow the guide and reference below.</p>

          <div className="skin-macro-zone-block">
            <h3>What we are capturing</h3>
            <p className="skin-macro-guide-lede">{guide.lede}</p>
          </div>

          <div className="skin-macro-refs">
            <p className="skin-macro-refs-label">
              <span aria-hidden>💡</span> Reference
            </p>
            <figure className="skin-macro-ref">
              <img src={guide.refImg} alt={`${regionName} microscope reference`} />
              <figcaption>{guide.refCaption}</figcaption>
            </figure>
          </div>

          <ul className="skin-macro-howto">
            <li>
              <span className="skin-macro-howto-ico skin-macro-howto-ico--focus" aria-hidden />
              <div>
                <strong>Focus on the {regionName.toLowerCase()}</strong>
                <p>Place the microscope on the {regionName.toLowerCase()} area.</p>
              </div>
            </li>
            <li>
              <span className="skin-macro-howto-ico skin-macro-howto-ico--steady" aria-hidden />
              <div>
                <strong>Keep it steady</strong>
                <p>Hold the device still for a clear shot.</p>
              </div>
            </li>
            <li>
              <span className="skin-macro-howto-ico skin-macro-howto-ico--light" aria-hidden />
              <div>
                <strong>Good lighting</strong>
                <p>Ensure adequate light for best clarity.</p>
              </div>
            </li>
          </ul>

          {micUnavailable && (
            <div className="skin-macro-warn">
              <strong>Microscope unavailable</strong>
              <p>You can continue with the face photo analysis.</p>
            </div>
          )}
          {error && <p className="skin-macro-error">{error}</p>}

          {!useNativeUsb && (
            <>
              <button
                type="button"
                className="skin-macro-btn ghost"
                onClick={() => setAdvancedOpen((v) => !v)}
              >
                {advancedOpen ? 'Hide Advanced' : 'Advanced'}
              </button>
              {advancedOpen && (
                <div className="skin-macro-advanced">
                  <label className="skin-macro-field">
                    Brightness
                    <input
                      type="range"
                      min="60"
                      max="160"
                      value={brightness}
                      onChange={(e) => setBrightness(Number(e.target.value))}
                    />
                  </label>
                  <button
                    type="button"
                    className="skin-macro-btn ghost"
                    onClick={() => {
                      if (!videoRef.current) return;
                      if (frozen) {
                        videoRef.current.play();
                        setFrozen(false);
                      } else {
                        videoRef.current.pause();
                        setFrozen(true);
                      }
                    }}
                  >
                    {frozen ? 'Unfreeze' : 'Freeze'}
                  </button>
                </div>
              )}
            </>
          )}
        </aside>

        <section className="skin-macro-tips" aria-label="Tips for best results">
          <h3>Tips for best results</h3>
          <ul>
            {MACRO_TIPS.map((tip) => (
              <li key={tip.id}>
                <span className={`skin-macro-tip-ico skin-macro-tip-ico--${tip.id}`} aria-hidden />
                <span>{tip.label}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="skin-macro-notice" role="note">
        <span aria-hidden>ⓘ</span>
        <p>
          {cameraAllowed !== true
            ? 'Tap Allow camera access so the tablet can open the USB microscope. The system permission prompt should appear after you tap.'
            : preview
            ? 'Check the close-up image. Tap Confirm to save this region, or Retake to capture again.'
            : useNativeUsb
            ? supportsNativeInlineScalpPreview()
              ? 'Live USB preview stays on this screen. Keep the target area centered, then tap Capture.'
              : 'Connect the USB microscope via OTG first, then tap Capture.'
            : 'Use the USB microscope preview on the left. Keep the target area centered and steady.'}
          {isLast && !preview ? ' This is the last skin detail — then analysis starts.' : ''}
        </p>
      </div>

      <div className="skin-macro-actions">
        {cameraAllowed !== true ? (
          <button
            type="button"
            className="skin-macro-btn primary skin-macro-btn-capture"
            disabled={allowBusy || cameraAllowed === null}
            onClick={onAllowCamera}
          >
            {allowBusy ? 'Waiting for permission…' : 'Allow camera access'}
          </button>
        ) : preview ? (
          <>
            <button
              type="button"
              className="skin-macro-btn ghost"
              disabled={busy}
              onClick={() => {
                setPreview(null);
                setError(null);
              }}
            >
              Retake
            </button>
            <button
              type="button"
              className="skin-macro-btn primary skin-macro-btn-capture"
              disabled={busy}
              onClick={() => void confirmPendingCapture()}
            >
              {busy ? 'Saving…' : isLast ? `Confirm ${regionName}` : `Confirm & next`}
            </button>
          </>
        ) : null}

        {cameraAllowed === true && !preview && useNativeUsb ? (
          <button
            type="button"
            className="skin-macro-btn primary skin-macro-btn-capture"
            disabled={captureBusy || !selectedNativeUsbId}
            onClick={captureUsbInline}
          >
            {busy || nativeOpening ? 'Capturing…' : nativeCooldown ? 'Ready…' : `Capture ${regionName}`}
          </button>
        ) : cameraAllowed === true && !preview && !micUnavailable ? (
          <button
            type="button"
            className="skin-macro-btn primary skin-macro-btn-capture"
            disabled={busy || !webPreviewReady}
            onClick={onCapture}
          >
            {busy ? 'Preparing…' : `Capture ${regionName}`}
          </button>
        ) : cameraAllowed === true && !preview ? (
          <button type="button" className="skin-macro-btn primary skin-macro-btn-capture" onClick={() => goToAnalysis(captures)}>
            Continue
          </button>
        ) : null}

        <button
          type="button"
          className="skin-macro-btn ghost skin-macro-btn-skip"
          disabled={busy || nativeOpening}
          onClick={handleSkipMacro}
        >
          Skip close-up — face report only
        </button>

        <button
          type="button"
          className="skin-macro-btn ghost"
          disabled={busy}
          onClick={() => fileInputRef.current?.click()}
        >
          Upload Photo Instead
        </button>

        {micUnavailable && !useNativeUsb && (
          <button type="button" className="skin-macro-btn ghost" onClick={() => startCamera(deviceId)}>
            Try Again
          </button>
        )}

        {micUnavailable && useNativeUsb && (
          <button type="button" className="skin-macro-btn ghost" onClick={() => goToAnalysis(captures)}>
            Continue without remaining detail
          </button>
        )}

        <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onUpload} />
      </div>

    </div>
  );
}
